import { datadogRum, type RumEvent } from '@datadog/browser-rum'
import { redactShareSecrets } from './redactShareSecrets'

export interface InitDatadogOptions {
  /** Empty / undefined applicationId or clientToken = SDK no-op (local dev, E2E builds). */
  applicationId: string | undefined
  clientToken: string | undefined
  service: string
  env: string | undefined
  version: string | undefined
}

/**
 * Whether `url` is a same-origin `/api/...` request — the shape every
 * in-app API call has, since every one of them goes through nginx on the
 * app's own origin.
 *
 * This is deliberately NOT just `/\/api\//.test(url)`: that regex also
 * matches a cross-origin URL that happens to contain `/api/` in its path —
 * e.g. the Apple identity resolver, which lives on a different origin in a
 * different repository. Handing that request Datadog's
 * `x-datadog-trace-id`/`x-datadog-parent-id`/`x-datadog-origin`/
 * `x-datadog-sampling-priority` headers turns a simple POST into one that
 * needs a CORS preflight, and the resolver's `Access-Control-Allow-Headers`
 * has no reason to list Datadog's headers — it's a different team's service
 * that has never heard of this app's RUM setup. The browser then blocks the
 * request outright, `fetch` rejects, and the resolver looks unreachable even
 * though it's up: the failure reads as "no affected users showed up" rather
 * than "RUM broke the request". Same-origin traffic through nginx has no
 * such CORS gate, which is why this bug pattern is specific to cross-origin
 * calls and had never shown up before this feature.
 */
export function isSameOriginApiUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin && /\/api\//.test(parsed.pathname)
  } catch {
    return false
  }
}

/**
 * The event fields that can carry a URL and that the SDK lets `beforeSend`
 * change (its `assembly.js` discards an edit to any other field). Long tasks'
 * `scripts[].source_url` also holds URLs, but only of the scripts that ran,
 * and the SDK treats it as read-only.
 */
const URL_BEARING_FIELDS: readonly (readonly string[])[] = [
  ['view', 'url'],
  ['view', 'referrer'],
  ['view', 'name'],
  ['resource', 'url'],
  ['error', 'message'],
  ['error', 'stack'],
  ['error', 'resource', 'url'],
  // The text of the clicked element, which on a share dialog can be the link.
  ['action', 'target', 'name'],
]

/**
 * Redacts share-link access keys from every URL-bearing field of a RUM event
 * (see `redactShareSecrets`) and keeps the event.
 *
 * It edits the event in place because that is the SDK's contract: `beforeSend`
 * returns only keep-or-drop, and a replacement object would be ignored.
 */
export function rumBeforeSend(event: RumEvent): boolean {
  for (const path of URL_BEARING_FIELDS) redactAt(event as unknown as Record<string, unknown>, path)
  return true
}

function redactAt(root: Record<string, unknown>, path: readonly string[]): void {
  const parent = path.slice(0, -1).reduce<unknown>(
    (node, key) => (typeof node === 'object' && node !== null ? (node as Record<string, unknown>)[key] : undefined),
    root,
  )
  if (typeof parent !== 'object' || parent === null) return
  const field = path[path.length - 1]
  const value = (parent as Record<string, unknown>)[field]
  if (typeof value !== 'string') return
  const redacted = redactShareSecrets(value)
  if (redacted !== value) (parent as Record<string, unknown>)[field] = redacted
}

/**
 * Initialise the Datadog browser RUM SDK with the WeHunt platform defaults:
 * errors-on-by-default tracking, no session replay (out of scope for v1),
 * masked user input, distributed-trace propagation on same-origin
 * /api/* calls, and share-link access keys redacted from every event.
 *
 * Safe to call once at app startup. Idempotent across re-renders.
 */
export function initDatadog({
  applicationId,
  clientToken,
  service,
  env,
  version,
}: InitDatadogOptions): void {
  if (!applicationId || !clientToken) return
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.eu',
    service,
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [{ match: isSameOriginApiUrl, propagatorTypes: ['datadog'] }],
    beforeSend: rumBeforeSend,
  })
}

export { datadogRum }
